import type React from 'react';

// a strict type definition for rgb color strings
type RGBColorString = `rgb(${number}, ${number}, ${number})`;

/**
 * This function returns a random color string in the format "rgb(r, g, b)"
 * This is meant to be used in conjunction with the getHighlightedRerendersStyle function.
 * This is not meant to be used in production, just a development tool.
 * @returns {string} the random color string
 * @example
 * const color = getRandomColor(); // returns 'rgb(255, 255, 255)'
 * const color = getRandomColor(); // returns 'rgb(0, 0, 0)'
 * const color = getRandomColor(); // returns 'rgb(255, 0, 0)'
 * const color = getRandomColor(); // returns 'rgb(0, 255, 0)'
 * // etc...
 */

const getRandomColor = (): RGBColorString => {
  // generate random values for r, g, and b between 0 and 255
  const r = Math.floor(Math.random() * 256);
  const g = Math.floor(Math.random() * 256);
  const b = Math.floor(Math.random() * 256);

  // return the color string in the format "rgb(r, g, b)"
  return `rgb(${r}, ${g}, ${b})`;
};

// type definition for getReadableColor

type GetReadableColor = (rgbColorString: RGBColorString) => RGBColorString;
/**
 * This function returns a readable color for the input color string.
 * This is meant to be used in conjunction with the getHighlightedRerendersStyle function.
 * This is not meant to be used in production, just a development tool.
 * @param {RGBColorString} rgbColorString the rgb color string to get a readable color for
 * @returns {RGBColorString} the readable color string
 * @example
 * const textColor = getReadableColor('rgb(255, 255, 255)'); // returns 'rgb(0, 0, 0)'
 * const textColor = getReadableColor('rgb(0, 0, 0)'); // returns 'rgb(255, 255, 255)'
 * const textColor = getReadableColor('rgb(255, 0, 0)'); // returns 'rgb(255, 255, 255)'
 * const textColor = getReadableColor('rgb(0, 255, 0)'); // returns 'rgb(0, 0, 0)'
 * // etc...
 */
const getReadableColor: GetReadableColor = (rgbColorString) => {
  // if the input color string is not in the correct format, throw an error
  if (!rgbColorString.match(/^rgb\(\d+, \d+, \d+\)$/)) {
    throw new Error(
      `The input color string "${rgbColorString}" is not in the correct format. The correct format is "rgb(r, g, b)" where r, g, and b are integers between 0 and 255.`,
    );
  }
  // extract the r, g, and b values from the string
  const [r, g, b] = rgbColorString.match(/\d+/g)?.map((numStr) => parseInt(numStr)) ?? [0, 0, 0];

  // calculate the relative luminance of the input color using the sRGB color space
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  // calculate the luminance of black and white colors
  const blackLuminance = 0;
  const whiteLuminance = 1;

  // calculate the luminance differences between the input color and black and white
  const luminanceDiffBlack = Math.abs(luminance - blackLuminance);
  const luminanceDiffWhite = Math.abs(luminance - whiteLuminance);

  // determine whether the input color is closer to black or white
  const textColor =
    luminanceDiffBlack > luminanceDiffWhite
      ? 'rgb(0, 0, 0)' // use white text for lighter colors
      : 'rgb(255, 255, 255)'; // use black text for darker colors

  // return the text color string
  return textColor;
};
// type definition for getHighlightedRerendersStyle
// this function returns an object with the style properties for the highlighted rerenders

type GetHighlightedRerendersStyle = () => Partial<React.CSSProperties>;
/**
 * This function returns an object with the style properties for highlighting rerenders.
 * this is meant to be invoked each time a component rerenders to get a new color combination
 * this is not meant to be used in production, just a development tool.
 * @returns {Partial<React.CSSProperties>} an object with the style properties for the highlighted rerenders
 */
export const getHighlightedRerendersStyle: GetHighlightedRerendersStyle = () => {
  const randColor = getRandomColor();
  return {
    backgroundColor: randColor,
    color: getReadableColor(randColor),
  };
};
